import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import HoverCopyField from '@/shein-platform/modules/products/components/HoverCopyField';

function formatPrice(val, currency) {
  if (val == null || val === '') return '—';
  const n = Number(val);
  if (Number.isNaN(n)) return String(val);
  // 平台货币由后端随价格一起给（TEMU 供货价是 CNY；SHEIN 行上没有该字段时保持旧展示）
  return `${currency || 'USD'} ${n.toFixed(2)}`;
}

function skuSizeLabel(sku) {
  return sku?.sizeName || sku?.sizeLabel || sku?.skuSpecLabel || sku?.specName || sku?.skuSize || '—';
}

/** 商家SKU：卖家自填货号；空则返回空串（勿回落平台 SKU ID） */
function skuMerchantCode(sku) {
  return sku?.supplierSku || sku?.sellerSku || sku?.merchantSku || sku?.supplierCode || '';
}

/**
 * SKC 下全部 SKU 明细弹窗
 *
 * <p>列表接口为了控体积，每个 SKC 只回 1 条预览 SKU 行（并带 {@code skuCount}）。
 * 传了 {@code loadSkus} 时，弹窗打开后才把全量明细拉回来——「进页面不加载详情数据」。
 */
export default function SkuDetailModal({
  open,
  skc = null,
  onClose,
  loadSkus = null,
  showPrice = true,
}) {
  if (!open || !skc) return null;
  return <SkuDetailModalContent skc={skc} loadSkus={loadSkus} onClose={onClose} showPrice={showPrice} />;
}

function SkuDetailModalContent({ skc, loadSkus, onClose, showPrice = true }) {
  const previewSkus = Array.isArray(skc.skus) ? skc.skus : [];
  const declaredCount = Number(skc.skuCount) > 0 ? Number(skc.skuCount) : previewSkus.length;
  const needsLoad = typeof loadSkus === 'function' && declaredCount > previewSkus.length;
  const [skus, setSkus] = useState(previewSkus);
  const [loading, setLoading] = useState(needsLoad);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!needsLoad) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    Promise.resolve()
      .then(() => loadSkus(skc))
      .then((rows) => {
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        // 明细拉失败/为空时保留预览行，弹窗不留空白
        if (list.length) setSkus(list);
        else setError('明细暂不可用，已展示预览');
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || '加载 SKU 明细失败');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsLoad, skc?.key]);

  const stop = (event) => {
    event.stopPropagation();
  };

  return createPortal(
    <div
      className="spo-sku-modal"
      role="presentation"
      onClick={onClose}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose?.();
      }}
    >
      <div className="spo-sku-modal__backdrop" aria-hidden="true" />
      <div
        className="spo-sku-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-sku-modal-title"
        onClick={stop}
      >
        <div className="spo-sku-modal__head">
          <div className="spo-sku-modal__head-main">
            <h2 id="spo-sku-modal-title">{fixedUi("SKU 明细")}</h2>
            <p className="spo-sku-modal__meta">
              {skc.specLabel ? (
                <>
                  <span className="spo-sku-modal__meta-text">{skc.specLabel}</span>
                  <span className="spo-sku-modal__meta-sep" aria-hidden="true">·</span>
                </>
              ) : null}
              <HoverCopyField
                label="SKC"
                value={skc.skcId && skc.skcId !== '—' ? skc.skcId : ''}
                separator="："
                className="spo-sku-modal__meta-copy"
              />
              <span className="spo-sku-modal__meta-text">
                共 {Math.max(declaredCount, skus.length)} 个尺码
                {loading ? '（明细加载中…）' : ''}
              </span>
            </p>
          </div>
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={onClose}>
            {fixedUi("关闭")}
          </button>
        </div>
        <div className="spo-sku-modal__body">
          {error ? <div className="spo-sku-modal__empty">{error}</div> : null}
          {skus.length === 0 ? (
            <div className="spo-sku-modal__empty">{loading ? fixedUi("明细加载中…") : fixedUi("暂无 SKU")}</div>
          ) : (
            <table className="spo-sku-modal__table">
              <thead>
                <tr>
                  <th scope="col">{fixedUi("尺码")}</th>
                  <th scope="col">{fixedUi("商家 SKU")}</th>
                  <th scope="col">{fixedUi("平台 SKU")}</th>
                  {showPrice ? <th scope="col">{fixedUi("价格")}</th> : null}
                  <th scope="col">{fixedUi("可用库存")}</th>
                  <th scope="col">{fixedUi("总库存")}</th>
                </tr>
              </thead>
              <tbody>
                {skus.map((sku, idx) => {
                  const merchantCode = skuMerchantCode(sku);
                  return (
                    <tr key={sku?.id || `${skc.key}-${idx}`}>
                      <td>{skuSizeLabel(sku)}</td>
                      <td>
                        <HoverCopyField value={merchantCode} />
                      </td>
                      <td>
                        <HoverCopyField
                          label="SKU"
                          value={sku?.platformSkuId || ''}
                        />
                      </td>
                      {showPrice ? (
                        <td>{formatPrice(sku?.salePrice ?? sku?.supplyPrice, sku?.currency)}</td>
                      ) : null}
                      <td>{sku?.stockAvailableQty ?? sku?.stockQty ?? '—'}</td>
                      <td>{sku?.stockQty ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="spo-sku-modal__foot">
          <button type="button" className="spo-btn spo-btn--primary spo-btn--sm" onClick={onClose}>
            知道了
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export { skuSizeLabel, skuMerchantCode };
